/**
 * Tipo de cambio hacia la moneda de reporte.
 *
 * Leerlo lo puede cualquier admin; fijarlo NO — el backend responde 403 con
 * `super_user_required`. La interfaz no adivina el rol: intenta y muestra lo
 * que conteste el servidor. Duplicar la regla de permisos en el navegador es
 * la forma clasica de que las dos versiones se desincronicen.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/api/client";

export interface FxRate {
  id: string;
  currency_from: string;
  currency_to: string;
  rate: number;
  effective_from: string;
  effective_until: string | null;
  source: string;
  updated_by: string | null;
  updated_by_username: string | null;
  updated_at: string;
  notes: string | null;
  is_current: boolean;
}

export interface FxRateList {
  current: FxRate[];
  history: FxRate[];
  /** Pares que la interfaz necesita y nadie ha cargado. */
  missing_pairs: string[];
  /** Si la sincronía diaria con Banxico está encendida. */
  banxico_activo?: boolean;
  banxico?: FxBanxicoEstado[];
}

export interface FxBanxicoEstado {
  par: string;
  serie: string;
  ultimo_fix: string | null;
  dias_habiles_sin_dato: number | null;
  /** Más de 3 días hábiles sin dato nuevo. */
  alerta: boolean;
}

export interface FxRateUpsert {
  currency_from: string;
  currency_to: string;
  rate: number;
  notes?: string | null;
}

const KEY = ["admin", "fx"] as const;

export function useFxRates() {
  return useQuery({
    queryKey: KEY,
    queryFn: () => api<FxRateList>("/api/v1/admin/fx"),
  });
}

export function useSetFxRate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: FxRateUpsert) =>
      api<FxRate>("/api/v1/admin/fx", { method: "PUT", body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY }); // incluye la serie
      // Los costos en pesos se recalculan en el servidor: hay que releerlos.
      qc.invalidateQueries({ queryKey: ["usage"] });
    },
  });
}

/** El tipo de cambio vigente de un par, o null si nadie lo ha cargado. */
export interface FxSeriePunto {
  effective_from: string;
  rate: number;
  /** "banxico" o "manual". */
  source: string;
  notes: string | null;
}

export interface FxSerie {
  desde: string;
  dias: number;
  pares: { par: string; puntos: FxSeriePunto[] }[];
}

/** Evolución de cada par en los últimos `dias` (más el que regía al empezar). */
export function useFxSerie(dias: number) {
  return useQuery({
    queryKey: [...KEY, "serie", dias],
    queryFn: () => api<FxSerie>(`/api/v1/admin/fx/serie?dias=${dias}`),
  });
}

/** "2026-10-30" → "30-oct-2026", sin pasar por la zona horaria del navegador. */
export function fechaCorta(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  const mes = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"][
    m - 1
  ];
  return `${String(d).padStart(2, "0")}-${mes}-${y}`;
}

export function currentRate(list: FxRateList | undefined, from: string, to: string): FxRate | null {
  return (
    list?.current.find((r) => r.currency_from === from && r.currency_to === to) ?? null
  );
}
